import React from "react";
import { PRINT_BASE, PRINT_TD, PRINT_TH, PrintHeader, printElement } from "../../../components/common/printSheet";
import { formatDate, money } from "../fuelFormat";

// Printed fuel refill authorisation (2026-10-28, "fuel refill request able to print once
// approved - top-up by vehicle type / new refill individual, direct cash"). APPROVED refills
// only. One refill prints on its own; a Top-up by Vehicle Type sheet has no batch number, so
// the page prints every approved refill in its current filter as one sheet, grouped by
// payment mode (card refills, then cash). Card refills show the card balance before -> after the approval.
const RIGHT = { textAlign: "right" };

const SignatureBlock = ({ label, name, date }) => (
  <div style={{ flex: 1 }}>
    <div style={{ fontSize: "10.5px", color: "#555" }}>{label}</div>
    <div style={{ fontSize: "11.5px", fontWeight: 700, minHeight: "16px" }}>{name || ""}</div>
    {date && <div style={{ fontSize: "10px", color: "#555" }}>{date}</div>}
    <div style={{ borderTop: "1px solid #000", marginTop: "26px", width: "85%", fontSize: "9.5px", color: "#555" }}>Signature</div>
  </div>
);

const FuelRefillSheet = ({ refills, branchCode, vehicleRegsFor, ownersFor, subtitle }) => {
  const groups = new Map();
  refills.forEach((r) => {
    const type = r.paymentMode === "CASH" ? "Cash for fuel" : "Card refills";
    if (!groups.has(type)) groups.set(type, []);
    groups.get(type).push(r);
  });
  const sortedGroups = [...groups.entries()].sort(([a], [b]) => b.localeCompare(a));
  const total = refills.reduce((s, r) => s + Number(r.refillAmount || 0), 0);
  const cashTotal = refills.filter((r) => r.paymentMode === "CASH").reduce((s, r) => s + Number(r.refillAmount || 0), 0);
  const single = refills.length === 1 ? refills[0] : null;
  const approvers = [...new Set(refills.map((r) => r.approvedByName).filter(Boolean))].join(", ");
  const requesters = [...new Set(refills.map((r) => r.requestedByName).filter(Boolean))].join(", ");
  let n = 0;

  return (
    <div style={{ ...PRINT_BASE, padding: "6px" }}>
      <PrintHeader
        title={single ? `Fuel Refill ${single.refillNumber}` : "Fuel Refills - Approved"}
        subtitle={subtitle}
        lines={[
          `${refills.length} refill${refills.length === 1 ? "" : "s"} · Total ${money(total)}${cashTotal > 0 ? ` (cash ${money(cashTotal)})` : ""}`,
        ]}
        branchCode={branchCode}
      />
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={PRINT_TH}>#</th>
            <th style={PRINT_TH}>Refill No</th>
            <th style={PRINT_TH}>Payment</th>
            <th style={PRINT_TH}>Card</th>
            <th style={PRINT_TH}>Vehicle</th>
            <th style={PRINT_TH}>Assigned to</th>
            <th style={PRINT_TH}>Date</th>
            <th style={PRINT_TH}>Reference</th>
            <th style={{ ...PRINT_TH, ...RIGHT }}>Balance before</th>
            <th style={{ ...PRINT_TH, ...RIGHT }}>Amount</th>
            <th style={{ ...PRINT_TH, ...RIGHT }}>Balance after</th>
          </tr>
        </thead>
        <tbody>
          {sortedGroups.map(([type, rows]) => (
            <React.Fragment key={type}>
              {sortedGroups.length > 1 && (
                <tr>
                  <td colSpan={11} style={{ ...PRINT_TD, fontWeight: 700, background: "#f3f3f3" }}>{type}</td>
                </tr>
              )}
              {rows.map((r) => {
                n += 1;
                const isCash = r.paymentMode === "CASH";
                return (
                  <tr key={r.recordGuid}>
                    <td style={PRINT_TD}>{n}</td>
                    <td style={PRINT_TD}>{r.refillNumber}</td>
                    <td style={PRINT_TD}>{isCash ? "Cash" : "Card"}</td>
                    <td style={PRINT_TD}>{isCash ? "-" : r.cardNumber}</td>
                    <td style={{ ...PRINT_TD, fontWeight: 700 }}>{vehicleRegsFor(r).join(", ") || "-"}</td>
                    <td style={{ ...PRINT_TD, fontWeight: 700, color: "#1d4ed8" }}>{ownersFor(r).join(", ")}</td>
                    <td style={PRINT_TD}>{formatDate(r.refillDate)}</td>
                    <td style={PRINT_TD}>{r.referenceNumber || ""}</td>
                    <td style={{ ...PRINT_TD, ...RIGHT }}>{isCash || r.previousBalance == null ? "" : money(r.previousBalance)}</td>
                    <td style={{ ...PRINT_TD, ...RIGHT, fontWeight: 700 }}>{money(r.refillAmount)}</td>
                    <td style={{ ...PRINT_TD, ...RIGHT }}>{isCash || r.newBalance == null ? "" : money(r.newBalance)}</td>
                  </tr>
                );
              })}
              {sortedGroups.length > 1 && (
                <tr>
                  <td colSpan={9} style={{ ...PRINT_TD, ...RIGHT, fontWeight: 700 }}>{type} total</td>
                  <td style={{ ...PRINT_TD, ...RIGHT, fontWeight: 700 }}>{money(rows.reduce((s, r) => s + Number(r.refillAmount || 0), 0))}</td>
                  <td style={PRINT_TD} />
                </tr>
              )}
            </React.Fragment>
          ))}
          <tr>
            <td colSpan={9} style={{ ...PRINT_TD, ...RIGHT, fontWeight: 700, borderTop: "1.5px solid #000" }}>Grand total</td>
            <td style={{ ...PRINT_TD, ...RIGHT, fontWeight: 700, borderTop: "1.5px solid #000" }}>{money(total)}</td>
            <td style={{ ...PRINT_TD, borderTop: "1.5px solid #000" }} />
          </tr>
        </tbody>
      </table>

      {single?.remarks && <div style={{ fontSize: "11px", marginTop: "8px" }}><b>Remarks:</b> {single.remarks}</div>}
      {single?.approvalComment && <div style={{ fontSize: "11px", marginTop: "4px" }}><b>Approval comment:</b> {single.approvalComment}</div>}

      <div style={{ display: "flex", gap: "16px", marginTop: "28px" }}>
        <SignatureBlock label="Requested by" name={requesters} date={single ? formatDate(single.createdDate) : null} />
        <SignatureBlock label="Approved by" name={approvers} date={single ? formatDate(single.approvedDate) : null} />
        <SignatureBlock label={cashTotal > 0 ? "Cash received by" : "Received by"} name="" />
      </div>
    </div>
  );
};

/** Prints the given APPROVED refills; returns false when the pop-up was blocked. */
export const printFuelRefills = (refills, options) =>
  printElement(<FuelRefillSheet refills={refills} {...options} />, {
    title: refills.length === 1 ? `Fuel Refill ${refills[0].refillNumber}` : "Fuel Refills",
    page: "A4 landscape",
  });
